import React, { useState } from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import gql from 'graphql-tag';
import useCurrentPage from '../../../hooks/useCurrentPage';
import StarRack from '../../../components/stateless/StarRack';
import { getBackgroundColor, getForegroundColor } from '../../../../src/web/utils/colorSelect';
import styles from './ReviewStars.scss';
import ReviewStarsModal from './ReviewStarsModal';

const propTypes = {
  backgroundColor: PropTypes.string,
  fontColor: PropTypes.string,
  starSize: PropTypes.string,
  starColor: PropTypes.string,
  enabledFacilities: PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
  }),
  redirect: PropTypes.string,
  facilities: PropTypes.arrayOf({
    id: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    reviewsCount: PropTypes.number.isRequired,
    pagePaths: PropTypes.arrayOf(PropTypes.shape({
      locale: PropTypes.string.isRequired,
    })).isRequired,
  }).isRequired,
  style: PropTypes.string,
  onCancelEdit: PropTypes.func.isRequired,
  onFinishEdit: PropTypes.func.isRequired,
  isEditing: PropTypes.bool,
  isEditable: PropTypes.bool,
  locale: PropTypes.string.isRequired,
};

const defaultProps = {
  isEditing: false,
  isEditable: false,
  backgroundColor: 0,
  fontColor: 4,
  starSize: 'medium',
  starColor: 1,
  enabledFacilities: {},
  redirect: '',
  style: '',
};

const ReviewStars = ({
  backgroundColor,
  fontColor,
  starSize,
  starColor,
  enabledFacilities,
  redirect,
  facilities,
  style,
  onCancelEdit,
  onFinishEdit,
  isEditing,
  isEditable,
  locale,
}) => {
  const [editing, setEditing] = useState(isEditing);

  const reviewCount = facilities.reduce((count, facility) => {
    if ((enabledFacilities
        && (!Object.keys(enabledFacilities).length || enabledFacilities[facility.id]))
        && facility.reviewsCount) {
      return count + facility.reviewsCount;
    }

    return count;
  }, 0);

  const getRatings = () => facilities.reduce((ratings, facility) => {
    if ((enabledFacilities
        && (!Object.keys(enabledFacilities).length || enabledFacilities[facility.id]))
        && facility.reviewsCount) {
      ratings.push(facility.rating);
    }
    return ratings;
  }, []);

  const avgRating = () => {
    const ratings = getRatings();
    const total = ratings.reduce((subtotal, rating) => subtotal + rating, 0);
    return Math.ceil(total / ratings.length);
  };

  const handleSave = (payload) => {
    onFinishEdit(payload);
    setEditing(false);
  };

  const hideModal = () => {
    setEditing(false);
  };

  const renderModal = () => (
    <ReviewStarsModal
      style={style}
      locale={locale}
      backgroundColor={backgroundColor}
      fontColor={fontColor}
      starColor={starColor}
      starSize={starSize}
      enabledFacilities={enabledFacilities}
      redirect={redirect}
      onCancelEdit={onCancelEdit}
      onClose={hideModal}
      onFinishEdit={handleSave}
      showModal={editing}
      rating={avgRating}
      facilities={facilities}
      reviewCount={reviewCount}
      avgRating={avgRating}
    />
  );

  const renderStars = () => {
    const context = useCurrentPage();
    const { foregroundClass } = getForegroundColor(fontColor, context);
    const { backgroundClass } = getBackgroundColor(backgroundColor, context);

    return (
      <div className={cx(styles.textContainer, backgroundClass)}>
        <div className={styles.stars}>
          <StarRack
            rating={avgRating}
            className={cx(styles[starSize], styles[starColor])}
          />
        </div>
        <span
          className={cx(styles.ratingsText, backgroundClass, foregroundClass)}
        >
          {reviewCount} Ratings
        </span>
      </div>
    );
  };

  return (
    <div>
      {redirect
        ? (
          // eslint-disable-next-line jsx-a11y/anchor-is-valid
          <a
            href={isEditable ? null : redirect}
            className={styles.anchor}
          >
            {renderStars()}
          </a>
        )
        : renderStars()
      }
      {editing ? renderModal() : null}
    </div>
  );
};

ReviewStars.propTypes = propTypes;
ReviewStars.defaultProps = defaultProps;

ReviewStars.fragments = {
  reviews: gql`
    fragment ReviewStars on Facility {
      rating
      reviewsCount
      ...ReviewStarsModalFacility
    }

    ${ReviewStarsModal.fragments.facility}
  `,
};

export default ReviewStars;
